import React, {useEffect, useState} from 'react';
import {Body, Button, Headline} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import CheckboxLabel from '@/components/checkbox-label';
import Modal from '@/components/modal';
import styles from './RequestEditsInterceptModal.module.scss';

type RequestEditsInterceptModalProps = {
  open: boolean;
  messagePreview: string;
  attachmentCount?: number;
  isLoading?: boolean;
  onRequestEdits: (dontAskAgain: boolean) => void;
  onSendAsMessage: (dontAskAgain: boolean) => void;
  onCancel: () => void;
};

function RequestEditsInterceptModal({
  open,
  messagePreview,
  attachmentCount = 0,
  isLoading = false,
  onRequestEdits,
  onSendAsMessage,
  onCancel,
}: RequestEditsInterceptModalProps) {
  const [dontAskAgain, setDontAskAgain] = useState(false);

  useEffect(() => {
    if (open) {
      setDontAskAgain(false);
    }
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={isLoading ? undefined : onCancel}
      size="small"
      contentClassName={styles.modalContent}
    >
      <div
        className={styles.modalInner}
        data-testid="request-edits-intercept-modal"
      >
        <Headline size="md" className={styles.modalTitle}>
          {translate('Request edits instead?')}
        </Headline>
        <Body size="md" className={styles.modalBody}>
          {translate(
            "Messaging doesn't update the asset status. Request Edits notifies the creator and moves this asset to Changes Requested."
          )}
        </Body>
        <div className={styles.preview}>
          <Body size="md" className={styles.previewText}>
            {messagePreview}
          </Body>
          {attachmentCount > 0 ? (
            <>
              <Body size="sm" className={styles.previewMeta}>
                {translate('attachment-count', {count: attachmentCount})}
              </Body>
              <Body size="sm" className={styles.attachmentWarning}>
                {translate(
                  "Attachments aren't included with Request Edits. Choose Send as message to include them."
                )}
              </Body>
            </>
          ) : null}
        </div>
        <div className={styles.checkboxRow}>
          <CheckboxLabel
            checked={dontAskAgain}
            onChange={setDontAskAgain}
            disabled={isLoading}
            label={translate("Don't ask me again")}
          />
        </div>
        <div className={styles.modalActions}>
          <Button
            appearance="neutral"
            mode="tinted"
            size="large"
            disabled={isLoading}
            onClick={onCancel}
          >
            {translate('Cancel')}
          </Button>
          <Button
            appearance="neutral"
            mode="tinted"
            size="large"
            disabled={isLoading}
            onClick={() => onSendAsMessage(dontAskAgain)}
          >
            {translate('Send as message')}
          </Button>
          <Button
            appearance="neutral"
            mode="filled"
            size="large"
            isLoading={isLoading}
            disabled={isLoading}
            onClick={() => onRequestEdits(dontAskAgain)}
          >
            {translate('Request Edits')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default RequestEditsInterceptModal;
